import { create } from "zustand";
import { persist } from "zustand/middleware";
import { useFirmContextStore } from "@/features/firm-context/store/firm-context-store";

export interface AuthUser {
  id: string;
  name: string;
  email: string;
  account_type: "firm" | "individual";
  // Ops/staff flag — gates the Settings → Reference Data admin area. Optional
  // so pre-existing persisted sessions (which predate it) still type-check
  // after rehydration; treated as false when absent.
  is_staff?: boolean;
  // Platform-admin (SaaS-operator) flag — gates the vendor Control Center
  // (/admin/*) and the ops tooling (/admin/ops/*) via PlatformAdminRoute.
  // Optional/absent → false, so pre-existing persisted sessions keep working
  // after rehydration.
  is_platform_admin?: boolean;
  // Django superuser — additionally gates the destructive ops actions
  // (cascade-delete a firm). Server enforces it regardless; this only hides the
  // UI. Optional/absent → false.
  is_superuser?: boolean;
  // Org-scoped role, the backend hierarchy (lowest→highest authority):
  // `staff` | `team_leader` | `country_director` (the org lead). Null for
  // individual/taxpayer accounts and platform GA/SA without an org role.
  role?: "staff" | "team_leader" | "country_director" | null;
  // Minimal firm identity nested in the login / verify-email / me payloads.
  // `onboarding_completed` drives the first-login onboarding gate (see
  // routes/onboarding-gate.tsx). `org_type` drives the role-aware sidebar view
  // (NSI house vs Business Practitioner). Optional throughout so pre-existing
  // persisted sessions (which predate these fields) still type-check after
  // rehydration; an absent `onboarding_completed` is treated as done, so the
  // gate only triggers on an explicit `false` from the server.
  firm?: {
    id: string;
    name: string;
    onboarding_completed?: boolean;
    org_type?: "house" | "business_practitioner";
  } | null;
  // Stored avatar path (private media), or null. A cache-busting token for the
  // authenticated `/auth/me/avatar` image endpoint — not a loadable URL itself.
  // Optional so pre-existing persisted sessions still type-check after rehydration.
  avatar?: string | null;
  language?: "en" | "es";
  time_zone?: string;
  email_signature?: string;
  default_practitioner?: string | null;
  default_practitioner_name?: string | null;
  // How many Business Practitioners this (house) user is assigned to oversee.
  // A house Staff member with ≥1 assignment gets the "Business Practitioners"
  // tab (scoped to those BPs server-side). Optional/absent → 0, so pre-existing
  // persisted sessions keep working after rehydration.
  assigned_bp_count?: number;
}

interface Session {
  user: AuthUser;
  accessToken: string;
  refreshToken: string;
}

/** The subset persisted to localStorage (see `partialize`). */
type PersistedAuth = Pick<AuthState, "user" | "accessToken" | "refreshToken" | "isAuthenticated">;

interface AuthState {
  user: AuthUser | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  /** Store a full session after login. */
  setSession: (session: Session) => void;
  /** Replace the cached user (e.g. after editing profile/preferences). */
  setUser: (user: AuthUser) => void;
  /** Swap in rotated tokens after a refresh (user unchanged). */
  setTokens: (accessToken: string, refreshToken: string) => void;
  /** Clear all auth state (logout / failed refresh). */
  clearSession: () => void;
}

/**
 * Global auth store. `persist` keeps the session in localStorage so a page
 * refresh doesn't kick the user back to the login screen. The tokens live
 * here (not the sensitive tax data itself) — see `lib/api.ts` for how they're
 * attached to requests and rotated.
 */
/**
 * The backend serializes the user PK as a number (`id: 5`), but the rest of the
 * app compares `user.id` against string ids (chat members/senders, etc.). Store
 * it as a string so strict `===` identity checks are correct everywhere.
 */
export function normalizeUser(user: AuthUser): AuthUser {
  return user.id == null ? user : { ...user, id: String(user.id) };
}

/**
 * Persist migration. Pre-v1 sessions (no tokens) are wiped so the user re-logs
 * in; v1→v2 coerces the persisted numeric user.id to a string while preserving
 * tokens + auth flag. Exported for direct test coverage.
 */
export function migrateAuthState(persisted: unknown, version: number): PersistedAuth {
  if (version < 1) {
    return { user: null, accessToken: null, refreshToken: null, isAuthenticated: false };
  }
  const state = persisted as PersistedAuth;
  if (state?.user) state.user = normalizeUser(state.user);
  return state;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      setSession: ({ user, accessToken, refreshToken }) =>
        set({ user: normalizeUser(user), accessToken, refreshToken, isAuthenticated: true }),
      setUser: (user) => set({ user: normalizeUser(user) }),
      setTokens: (accessToken, refreshToken) => set({ accessToken, refreshToken }),
      clearSession: () => {
        // End-of-session chokepoint (logout / failed refresh): drop the active
        // firm workspace too, so the next user in this tab starts in-house and a
        // stale firm id can't scope their data. The store is in-memory, so a full
        // reload would also reset it — but logout is a SPA navigate, not a reload.
        useFirmContextStore.getState().clearFirm();
        set({ user: null, accessToken: null, refreshToken: null, isAuthenticated: false });
      },
    }),
    {
      name: "auth-storage",
      // v1 introduced token storage. Pre-v1 sessions were `{ user,
      // isAuthenticated }` with no tokens — rehydrating those would leave a
      // user "authenticated" with null tokens (every request 401s, no
      // redirect). Drop them so the user simply re-logs in.
      // v2 coerces the persisted user.id to a string (it used to be stored as
      // the numeric PK), so identity `===` checks against string ids — chat
      // message authorship, group roles — work without forcing a re-login.
      version: 2,
      migrate: (persisted, version) => migrateAuthState(persisted, version),
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
);
